var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var _a, _b;
import { createPublicClient, http } from 'viem';
import { arbitrum, arbitrumGoerli } from 'viem/chains';
import { ARBITRUM, ARBITRUM_GOERLI } from '../constants';
import { getFallbackRpcUrl, getRpcUrl } from '../utils';
import CustomErrors from './CustomErrors.json';
export var MAX_TIMEOUT = 2000;
export function sleep(ms) {
    return new Promise(function (resolve) {
        setTimeout(function () { return resolve(); }, ms);
    });
}
var CHAIN_BY_CHAIN_ID = (_a = {},
    _a[ARBITRUM_GOERLI] = arbitrumGoerli,
    _a[ARBITRUM] = arbitrum,
    _a);
var BATCH_CONFIGS = (_b = {},
    _b[ARBITRUM] = {
        http: {
            batchSize: 0, // disable batches, here batchSize is the number of eth_calls in a batch
            wait: 0, // keep this setting in case batches are enabled in future
        },
        client: {
            multicall: {
                batchSize: 1024 * 1024, // here batchSize is the number of bytes in a multicall
                wait: 0, // zero delay means formation of a batch in the current macro-task, like setTimeout(fn, 0)
            },
        },
    },
    _b[ARBITRUM_GOERLI] = {
        http: {
            batchSize: 0,
            wait: 0,
        },
        client: {
            multicall: {
                batchSize: 1024 * 1024,
                wait: 0,
            },
        },
    },
    _b);
export function executeMulticall(chainId, library, request) {
    return __awaiter(this, void 0, void 0, function () {
        var multicall;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, Multicall.getInstance(chainId)];
                case 1:
                    multicall = _a.sent();
                    return [2 /*return*/, multicall === null || multicall === void 0 ? void 0 : multicall.call(request, MAX_TIMEOUT)];
            }
        });
    });
}
var Multicall = /** @class */ (function () {
    function Multicall(chainId, rpcUrl) {
        this.chainId = chainId;
        this.rpcUrl = rpcUrl;
        this.viemClient = Multicall.getViemClient(chainId, rpcUrl);
    }
    Multicall.getInstance = function (chainId) {
        return __awaiter(this, void 0, void 0, function () {
            var instance, rpcUrl;
            return __generator(this, function (_a) {
                instance = Multicall.instances[chainId];
                if (!instance || instance.chainId !== chainId) {
                    rpcUrl = getRpcUrl(chainId);
                    if (!rpcUrl) {
                        return [2 /*return*/, undefined];
                    }
                    instance = new Multicall(chainId, rpcUrl);
                    Multicall.instances[chainId] = instance;
                }
                return [2 /*return*/, instance];
            });
        });
    };
    Multicall.getViemClient = function (chainId, rpcUrl) {
        return createPublicClient({
            transport: http(rpcUrl, {
                // retries works strangely in viem, so we disable them
                retryCount: 0,
                retryDelay: 10000000,
                batch: BATCH_CONFIGS[chainId].http,
            }),
            pollingInterval: undefined,
            batch: BATCH_CONFIGS[chainId].client,
            chain: CHAIN_BY_CHAIN_ID[chainId],
        });
    };
    Multicall.prototype.call = function (request, maxTimeout) {
        return __awaiter(this, void 0, void 0, function () {
            var originalKeys, abis, encodedPayload, contractKeys, response, multicallResult;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        originalKeys = [];
                        abis = {};
                        encodedPayload = [];
                        contractKeys = Object.keys(request);
                        contractKeys.forEach(function (contractKey) {
                            var contractCallConfig = request[contractKey];
                            if (!contractCallConfig) {
                                return;
                            }
                            Object.keys(contractCallConfig.calls).forEach(function (callKey) {
                                var call = contractCallConfig.calls[callKey];
                                if (!call) {
                                    return;
                                }
                                // Add Errors ABI to each contract ABI to correctly parse errors
                                abis[contractCallConfig.contractAddress] =
                                    abis[contractCallConfig.contractAddress] || contractCallConfig.abi.concat(CustomErrors.abi);
                                var abi = abis[contractCallConfig.contractAddress];
                                originalKeys.push({
                                    contractKey: contractKey,
                                    callKey: callKey,
                                });
                                encodedPayload.push({
                                    address: contractCallConfig.contractAddress,
                                    functionName: call.methodName,
                                    abi: abi,
                                    args: call.params,
                                });
                            });
                        });
                        return [4 /*yield*/, Promise.race([
                                this.viemClient.multicall({ contracts: encodedPayload }),
                                sleep(maxTimeout).then(function () { return Promise.reject(new Error('multicall timeout')); }),
                            ]).catch(function (_viemError) {
                                var e = new Error(_viemError.message.slice(0, 150));
                                // eslint-disable-next-line no-console
                                console.groupCollapsed('multicall error:');
                                // eslint-disable-next-line no-console
                                console.error(e);
                                // eslint-disable-next-line no-console
                                console.groupEnd();
                                var rpcUrl = getFallbackRpcUrl(_this.chainId);
                                if (!rpcUrl) {
                                    throw e;
                                }
                                var fallbackClient = Multicall.getViemClient(_this.chainId, rpcUrl);
                                // eslint-disable-next-line no-console
                                console.log("using multicall fallback for chain ".concat(_this.chainId));
                                return fallbackClient.multicall({ contracts: encodedPayload }).catch(function (_viemError) {
                                    var e = new Error(_viemError.message.slice(0, 150));
                                    // eslint-disable-next-line no-console
                                    console.groupCollapsed('multicall fallback error:');
                                    // eslint-disable-next-line no-console
                                    console.error(e);
                                    // eslint-disable-next-line no-console
                                    console.groupEnd();
                                    throw e;
                                });
                            })];
                    case 1:
                        response = _a.sent();
                        multicallResult = {
                            success: true,
                            errors: {},
                            data: {},
                        };
                        response.forEach(function (_a, i) {
                            var result = _a.result, status = _a.status, error = _a.error;
                            var _b = originalKeys[i], contractKey = _b.contractKey, callKey = _b.callKey;
                            if (status === 'success') {
                                var values = void 0;
                                if (Array.isArray(result) || typeof result === 'object') {
                                    values = result;
                                }
                                else {
                                    values = [result];
                                }
                                multicallResult.data[contractKey] = multicallResult.data[contractKey] || {};
                                multicallResult.data[contractKey][callKey] = {
                                    contractKey: contractKey,
                                    callKey: callKey,
                                    returnValues: values,
                                    success: true,
                                };
                            }
                            else {
                                multicallResult.success = false;
                                multicallResult.errors[contractKey] = multicallResult.errors[contractKey] || {};
                                multicallResult.errors[contractKey][callKey] = error;
                                multicallResult.data[contractKey] = multicallResult.data[contractKey] || {};
                                multicallResult.data[contractKey][callKey] = {
                                    contractKey: contractKey,
                                    callKey: callKey,
                                    returnValues: [],
                                    success: false,
                                    error: error,
                                };
                            }
                        });
                        return [2 /*return*/, multicallResult];
                }
            });
        });
    };
    Multicall.instances = {};
    return Multicall;
}());
export { Multicall };
